import { useCallback, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Trans, useLingui } from '@lingui/react/macro'
import { Mail01Icon } from '@hugeicons/core-solid-rounded'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import { dialogContainer, DialogHeader } from '@/layouts/Dialog'
import { AddButton } from '@/components/ui/IconButton'
import Button from '@/components/ui/Button'
import { useOrganization } from '@/features/organization'
import Dropdown from '@/components/ui/Dropdown'
import { ROLES } from '../utils/helpers'
import Input from '@/components/ui/Input'

type CreateProps = {
  onDone: () => void
  onCancel: () => void
}

type FormData = {
  role: GraphQL.Role
  email: string
}

function AddMemberContent({ onDone, onCancel }: CreateProps) {
  const { t } = useLingui()
  const { organization } = useOrganization()

  const [createInvite] = useMutation(AdminAPI.membershipInvite.CREATE, {
    refetchQueries: [AdminAPI.membershipInvite.GET]
  })

  const { errors, handlePromiseCatch } = Form.useErrorHelpers<FormData>({
    mapper(field, message) {
      if (field === 'email' && message === 'has already been taken') {
        return [
          field,
          {
            type: message,
            message: t`The E-Mail is already a user`
          }
        ]
      }
    }
  })

  const create = useCallback(
    (data: FormData) => {
      return createInvite({
        variables: {
          input: data
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone?.()
        })
        .catch(error => handlePromiseCatch(error))
    },
    [onDone, createInvite, handlePromiseCatch]
  )

  const organizationName = organization.name

  return (
    <Form.Root<FormData>
      onSubmit={create}
      serverErrors={errors}
      defaultValues={{ role: GraphQL.Role.Member, email: '' }}
    >
      <div className="flex flex-col gap-6 p-2.5">
        <DialogHeader
          title={t`Invite Member`}
          description={t`Create a new member for the Hotel "${organizationName}"`}
          close={onCancel}
        />
        <div className="flex flex-col pb-3 gap-6">
          <Form.Field
            name="email"
            label={t`Email`}
            rules={{
              validate: {
                emailValidation: Form.Validations.emailValidation(
                  t`Not a valid email.`
                ),
                required: Form.Validations.requiredWithTrimValidation(
                  t`This field is required`
                )
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                type="email"
                autoFocus
                Icon={Mail01Icon}
                onChange={event => onChange(event.target.value)}
                placeholder=""
                {...props}
              />
            )}
          </Form.Field>
          <Form.Field
            name="role"
            label={t`Member role`}
            rules={{
              required: t`This field is required`
            }}
          >
            {({ renderProps: props }) => (
              <Dropdown options={ROLES} {...props} />
            )}
          </Form.Field>
        </div>
        <footer className="flex flex-row items-center justify-end gap-4">
          <Button variant="outline" onClick={onCancel} type="button">
            <Trans>Cancel</Trans>
          </Button>
          <Form.Submit>
            <Trans>Invite</Trans>
          </Form.Submit>
        </footer>
      </div>
    </Form.Root>
  )
}

export function AddMemberButton() {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <AddButton aria-label={t`Add member`} />
      </Dialog.Trigger>
      <Dialog.Portal container={dialogContainer}>
        <Dialog.Overlay className="fixed size-full inset-0 bg-black/30 grid place-items-center overflow-y-auto z-top-dialog">
          <Dialog.Content className="relative w-screen max-w-[500px] py-10 px-2">
            <div className="shadow-md rounded bg-white flex flex-col gap-4 p-5">
              <AddMemberContent
                onDone={() => setOpen(false)}
                onCancel={() => setOpen(false)}
              />
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
